import React from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Grid,
  Tooltip,
} from "@mui/material";
import EmptyResult from "components/Shared/Lottie/EmptyResult";
import {
  ContactPhone,
  Call,
  ModeEdit,
  ToggleOff,
  ToggleOn,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import FormDrawer from "components/Shared/FormDrawer";
import AdditionalNumberForm from "./AdditionalNumberForm";
import {
  useUpdateBranchNumberMutation,
  useUpdateBranchNumberStatusMutation,
} from "store/services/branchApi";
import { useAuth } from "hooks";

const NumberCard = ({ numbers = [], branchDetail }) => {
  const validNumbers = numbers.filter((number) => number?.name?.trim());

  const [showNumberForm, setShowNumberForm] = React.useState(false);

  const [updateNumber, { isLoading: updatingNumber }] =
    useUpdateBranchNumberMutation();

  const { isAdmin } = useAuth();
  const handleUpdateNumber = async (data) => {
    try {
      const _payload = {
        ...data,
        branchId: branchDetail?._id,
      };
      await updateNumber(_payload).unwrap();
      toast.success("Number updated successfully");
      setShowNumberForm(false);
    } catch (err) {
      toast.error(err?.data?.message ?? err?.message ?? "Couldn't add Number!");
    }
  };

  const handleCancelNumber = () => {
    setShowNumberForm(false);
  };

  const [updateNumberStatus, { isLoading: updatingStatus }] =
    useUpdateBranchNumberStatusMutation();

  const handleToggleStatus = async (branchId, numberId, currentStatus) => {
    try {
      const updatedStatus = !currentStatus;

      await updateNumberStatus({
        branchId,
        numberId,
        isActive: updatedStatus,
      }).unwrap();

      if (updatedStatus) {
        toast.success("Number activated successfully!");
      } else {
        toast.success("Number deactivated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update status. Please try again.");
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {validNumbers.length > 0 ? (
          validNumbers.map((number) => (
            <Card
              key={number?._id}
              className="transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl"
              sx={{
                borderRadius: 2,
                height: "auto",
                minHeight: "auto",
                "&:hover": {
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              <CardContent
                sx={{
                  position: "relative",
                  padding: 1,
                  marginBottom: -2,
                }}
              >
                <div className="mb-2 flex items-center gap-3">
                  <ContactPhone sx={{ fontSize: 13, color: "primary.main" }} />
                  <Typography variant="body2" className="font-medium">
                    {number?.name}
                  </Typography>
                </div>
                <div className="mb-2 flex items-center gap-3">
                  <Call sx={{ fontSize: 13, color: "primary.main" }} />
                  <Typography variant="body2" className="font-medium">
                    {number?.phoneNumber}
                  </Typography>
                </div>
                {isAdmin && (
                  <>
                    <Tooltip title="Edit Number" arrow>
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 6,
                        }}
                        onClick={() => setShowNumberForm(number)}
                      >
                        <ModeEdit />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={number?.isActive ? "Deactivate Number" : "Activate Number"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 35,
                          right: 6,
                        }}
                        onClick={() =>
                          handleToggleStatus(
                            branchDetail?._id,
                            number?._id,
                            number?.isActive
                          )
                        }
                        disabled={updatingStatus}
                      >
                        {number?.isActive ? (
                          <ToggleOn color="success" />
                        ) : (
                          <ToggleOff color="error" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </>
                )}

              </CardContent>
            </Card>
          ))
        ) : (
          <EmptyResult height={150} />
        )}
      </div>
      <FormDrawer
        title="Edit Additional Number"
        open={showNumberForm}
        onClose={handleCancelNumber}
      >
        <AdditionalNumberForm
          numberDetail={showNumberForm}
          isBusy={updatingNumber}
          onSubmit={(data) => handleUpdateNumber(data)}
          onCancel={handleCancelNumber}
        />
      </FormDrawer>
    </>
  );
};

const AdditionalNumberList = ({ number = [], branchDetail }) => (
  <Grid item xs={12}>
    <Box p={2}>
      {number?.length > 0 ? (
        <NumberCard numbers={number} branchDetail={branchDetail} />
      ) : (
        <EmptyResult height={150} />
      )}
    </Box>
  </Grid>
);

export default AdditionalNumberList;
